// https://annuities.myaccount.pacificlife.com/enroll-ui/client
import React, { useState, useEffect, useRef } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import ReactTooltip from "react-tooltip";
import Toast from "react-bootstrap/Toast";
import axios from "axios";
import moment from "moment";
import Enumerable from "linq";
import { QuestionCircle } from "react-bootstrap-icons";
import { REQUIRED_FIELDS, OHH_AUTOREG } from "./constants";
import { userNameValidation } from "./validation";
import { analytics } from "./analytics";
import { Header, Footer, DisclaimerPolicyOwner } from "./components";
import TermsAndConditions from "./Terms&Conditions";
import "react-datepicker/dist/react-datepicker.css";
import { getIPAddress } from "./utils/getIPAddress";

const OHHAutoReg = () => {
    const parseDateOnlyAsLocalDate = (dateString) => {
        if (!dateString || typeof dateString !== "string") {
            return null;
        }

        const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (!match) {
            return null;
        }

        const year = Number(match[1]);
        const monthIndex = Number(match[2]) - 1;
        const day = Number(match[3]);
        const parsedDate = new Date(year, monthIndex, day);

        if (
            parsedDate.getFullYear() !== year ||
            parsedDate.getMonth() !== monthIndex ||
            parsedDate.getDate() !== day
        ) {
            return null;
        }

        return parsedDate;
    };

    const searchParams = new URLSearchParams(window.location.search);
    const payload = searchParams.get("payload");
    let decodedPayload = [];
    if (payload) {
        try {
            decodedPayload = JSON.parse(atob(payload));
        } catch (e) {
            decodedPayload = [];
        }
    }

    const getDecodedPayload = (decodedPayload) => {
        const rawDob = Enumerable.from(decodedPayload)
            .where((x) => x.key === "dob" || x.Key === "dob")
            .select((x) => x.value || x.Value)
            .firstOrDefault();
        const invitationCode = Enumerable.from(decodedPayload)
            .where(
                (x) => x.key === "invitationCode" || x.Key === "invitationCode"
            )
            .select((x) => x.value || x.Value)
            .firstOrDefault();

        return decodedPayload
            ? {
                  firstName: Enumerable.from(decodedPayload)
                      .where(
                          (x) => x.key === "firstname" || x.Key === "firstname"
                      )
                      .select((x) => x.value || x.Value)
                      .firstOrDefault(),
                  lastName: Enumerable.from(decodedPayload)
                      .where(
                          (x) => x.key === "lastname" || x.Key === "lastname"
                      )
                      .select((x) => x.value || x.Value)
                      .firstOrDefault(),
                  middleName: Enumerable.from(decodedPayload)
                      .where(
                          (x) =>
                              x.key === "middlename" || x.Key === "middlename"
                      )
                      .select((x) => x.value || x.Value)
                      .firstOrDefault(),
                  suffix: Enumerable.from(decodedPayload)
                      .where((x) => x.key === "suffix" || x.Key === "suffix")
                      .select((x) => x.value || x.Value)
                      .firstOrDefault(),
                  email: Enumerable.from(decodedPayload)
                      .where((x) => x.key === "email" || x.Key === "email")
                      .select((x) => x.value || x.Value)
                      .firstOrDefault(),
                  policyNumber: Enumerable.from(decodedPayload)
                      .where(
                          (x) =>
                              x.key === "policynumber" ||
                              x.Key === "policynumber"
                      )
                      .select((x) => x.value || x.Value)
                      .firstOrDefault(),
                  invitationCode: invitationCode || "",
                  dob: parseDateOnlyAsLocalDate(rawDob),
                  tenant: Enumerable.from(decodedPayload)
                      .where((x) => x.key === "tenant" || x.Key === "tenant")
                      .select((x) => x.value || x.Value)
                      .firstOrDefault()
              }
            : {};
    };

    const initialUser = getDecodedPayload(decodedPayload);
    const [user, setUser] = useState({
        firstName: initialUser.firstName || "",
        lastName: initialUser.lastName || "",
        email: initialUser.email || "",
        userName: "",
        trustName: "",
        invitationCode: initialUser.invitationCode || "",
        dob: initialUser.dob || null,
        policyNumber: initialUser.policyNumber || "",
        middleName: initialUser.middleName || "",
        suffix: initialUser.suffix || "",
        isCheckedFindMyPolicy: false,
        tinType: "P", // value "T" displays Trust/Organization field
        tenantRegistrationState: ""
    });
    const [termsAndConditionsOpen, setTermsAndConditionsOpen] = useState(false);
    const [errors, setErrors] = useState({
        userName: "",
        trustName: "",
        middleName: "",
        suffix: "",
        isCheckedFindMyPolicy: false,
        tinType: ""
    });
    const [toastMessage, setToastMessage] = useState("");
    const [submitDisabled, setSubmitDisabled] = useState(true);
    const [submitting, setsubmitting] = useState(false);
    const [tinDisplay, setTinDisplay] = useState("");
    const [submitSuccessful, setSubmitSuccessful] = useState(false);
    const [showSuccessOverlay, setShowSuccessOverlay] = useState(false);
    const [successRedirectUrl, setSuccessRedirectUrl] = useState("");
    const redirectTimerRef = useRef(null);
    const [isMiddleNameDisabled] = useState(
        Boolean(initialUser?.middleName?.trim())
    );
    const [isSuffixDisabled] = useState(Boolean(initialUser?.suffix?.trim()));

    const setTermsAndConditions = (e) => {
        setTermsAndConditionsOpen(true);
    };

    const closeTermsAndConditions = (value) => {
        setTermsAndConditionsOpen(value);
    };

    useEffect(() => {
        analytics();
    }, []);

    useEffect(() => {
        const role = OHH_AUTOREG;
        const requiredFields = REQUIRED_FIELDS[role];
        for (const element of requiredFields) {
            const fieldName = element;
            if (!user[fieldName]) {
                setSubmitDisabled(true);
                return;
            }
        }

        const errorFields = Object.keys(errors);
        for (const element of errorFields) {
            const fieldName = element;
            if (errors[fieldName] !== "") {
                setSubmitDisabled(true);
                return;
            }
        }

        setSubmitDisabled(false);
    }, [user, errors]);

    useEffect(() => {
        document.title = "Policy Owner Registration | Pacific Life";
    }, []);

    useEffect(() => {
        return () => {
            if (redirectTimerRef.current) {
                clearTimeout(redirectTimerRef.current);
            }
        };
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        const payload = { [name]: value };
        setUser((prev) => ({ ...prev, ...payload }));
    };

    const handleRadioChange = async (e) => {
        const nameRd = e.target.name;
        const valueRd = e.target.value;
        const payloadRd = { [nameRd]: valueRd };
        await handleBlur(e);
        setUser((prev) => ({ ...prev, ...payloadRd }));
    };

    const handleCheckboxChange = (e) => {
        const name = e.target.name;
        const value = e.target.checked;
        const payload = { [name]: value };
        setUser((prev) => ({ ...prev, ...payload }));
    };

    const handleBlur = async (e) => {
        const { name, value } = e.target;
        let errorMessage = "";

        if (name === "trustName") {
            if (user.tinType === "T") {
                if (value) {
                    if (/[^[a-zA-Z0-9\s()&,''-]/.test(value)) {
                        errorMessage =
                            "Trust/Organization entered is not valid";
                    }
                }
            }
        }

        if (name === "isCheckedFindMyPolicy") {
            errorMessage = e.target.checked
                ? ""
                : "Agree to the terms and conditions";
        }

        if (name === "tinType") {
            errorMessage = value ? "" : "Select SSN or TIN/EIN";
        }

        if (name === "firstName") {
            if (value.length === 0) {
                errorMessage = "First Name must be entered";
            } else if (/[^a-zA-Z-. ]/.test(value)) {
                errorMessage =
                    "First Name must not contain numbers or special characters";
            } else {
                errorMessage = "";
            }
        }

        if (name === "middleName") {
            if (/[^a-zA-Z-. ]/.test(value)) {
                errorMessage =
                    "Middle Name must not contain numbers or special characters";
            } else {
                errorMessage = "";
            }
        }

        if (name === "suffix") {
            if (/[^a-zA-Z-. ]/.test(value)) {
                errorMessage =
                    "Suffix must not contain numbers or special characters";
            } else {
                errorMessage = "";
            }
        }

        if (name === "lastName") {
            if (value.length === 0) {
                errorMessage = "Last Name must be entered";
            } else if (/[^a-zA-Z-. ]/.test(value)) {
                errorMessage =
                    "Last Name must not contain numbers or special characters";
            } else {
                errorMessage = "";
            }
        }

        if (name === "id") {
            const validUserName = userNameValidation(value);
            const tooShortOrLongUserName =
                value.length < 6 || value.length > 15;
            if (value.indexOf(" ") > -1) {
                errorMessage = "Username name should not contain space";
            }
            if (/[^a-zA-Z0-9-. ]/.test(value)) {
                errorMessage = "Username must not contain special characters";
            }
            if (validUserName) {
                errorMessage = "";
            } else if (tooShortOrLongUserName) {
                errorMessage =
                    "Username must be between 6 and 15 characters long";
            }
        }
        if (errorMessage !== "") {
            setSubmitDisabled(true);
        } else {
            setSubmitDisabled(false);
        }
        setErrors((prev) => ({ ...prev, [name]: errorMessage }));
    };

    const formatTinDigits = (digits) => {
        if (digits.length <= 3) return digits;
        if (digits.length <= 5)
            return `${digits.slice(0, 3)}-${digits.slice(3)}`;
        return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
    };

    const maskTin = (digits) => {
        if (digits.length === 0) return "";
        const formatted = formatTinDigits(digits);
        const visibleDigits = 4;
        const maskCount = Math.max(0, digits.length - visibleDigits);
        let seen = 0;
        return formatted.replace(/\d/g, (ch) => {
            seen++;
            return seen <= maskCount ? "*" : ch;
        });
    };

    const handleTinChange = (e) => {
        const raw = e.target.value.replace(/[^0-9-]/g, "");
        const digits = raw.replace(/\D/g, "").slice(0, 9);
        const formatted = formatTinDigits(digits);
        // Preserve a trailing dash when typed at a valid group boundary (after 3 or 5 digits)
        const trailingDash =
            raw.endsWith("-") && (digits.length === 3 || digits.length === 5);
        setTinDisplay(trailingDash ? `${formatted}-` : formatted);
        setUser((prev) => ({ ...prev, tin: digits }));
    };

    const handleTinFocus = () => {
        setTinDisplay(formatTinDigits(user.tin || ""));
    };

    const handleTinBlur = (e) => {
        setTinDisplay(maskTin(user.tin || ""));
        handleBlur(e);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (submitSuccessful) {
            return;
        }

        let errMsg = "";
        //const test = JSON.stringify(payload);
        setsubmitting(true);

        const payload = await formatPayloadProfile();
        try {
            const res = await axios({
                method: "POST",
                url: "/life/enroll/api/userenroll/ohh/enroll/",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json"
                },
                data: JSON.stringify(payload)
            }).catch(function (err) {
                if (err.response) {
                    errMsg = err.response?.data?.error?.message;
                    console.log(
                        err.response?.data?.error?.message ||
                            err.response?.data ||
                            "Something went wrong.  Please try again later."
                    );
                    setToastMessage(
                        errMsg ||
                            "Something went wrong. Please try again later."
                    );
                } else if (err.request) {
                    console.log(err.request);
                } else {
                    console.log("Error", err.message);
                }
            });
            if (res.status === 200) {
                if (res.data.state) {
                    user.tenantRegistrationState = res.data.state;
                } else {
                    const redirectUrl = res.data?.data?.link;

                    if (!redirectUrl) {
                        setToastMessage(
                            "Registration was successful, but redirect link is unavailable."
                        );
                        setsubmitting(false);
                        return;
                    }

                    setSuccessRedirectUrl(redirectUrl);
                    setSubmitSuccessful(true);
                    setShowSuccessOverlay(true);
                    redirectTimerRef.current = setTimeout(() => {
                        window.location.href = redirectUrl;
                    }, 5000);
                }
            }
        } catch (error) {
            setToastMessage(
                errMsg ||
                    error.response?.data?.errorMessage ||
                    "Something went wrong. Please try again later."
            );
        }
        setsubmitting(false);
    };

    const formatPayloadProfile = async () => {
        const fallbackPhoneNumber = "9495551212";

        return {
            firstName: user.firstName,
            lastName: user.lastName,
            ssnTin: user.tin,
            secondaryPhone: user.secondaryPhoneNumber,
            createdBy: user.id,
            email: user.email.trim(),
            phoneNumber: user.phoneNumber || fallbackPhoneNumber,
            userId: user.id,
            trustName: user.trustName,
            invitationCode: user.invitationCode,
            role: "client",
            middleName: user.middleName,
            tinType: user.tinType,
            password: user.password,
            policyNumber: user.policyNumber,
            ipAddress: await getIPAddress(),
            suffix: user.suffix,
            dob: moment(user.dob).format("YYYY-MM-DD"),
            tenant: "ohh"
        };
    };

    const closeToast = () => {
        setToastMessage("");
    };

    return (
        <>
            <div
                style={
                    showSuccessOverlay
                        ? {
                              filter: "blur(4px)",
                              pointerEvents: "none",
                              userSelect: "none"
                          }
                        : undefined
                }
            >
                {toastMessage !== "" && (
                    <Toast onClose={closeToast} className="toast-cont">
                        <Toast.Header>
                            <strong className="me-auto">
                                <div
                                    dangerouslySetInnerHTML={{
                                        __html: toastMessage
                                    }}
                                ></div>
                            </strong>
                        </Toast.Header>
                    </Toast>
                )}
                <Header />
                <div className="mainMyAccount">
                    <h1 className="heading">Register</h1>
                    <Form>
                        <Row>
                            <Col sm={12}>
                                <label className="sub-title">
                                    Policy Information
                                </label>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={3}>
                                <input
                                    type="radio"
                                    name="tinType"
                                    value="P"
                                    onChange={handleRadioChange}
                                    checked={user.tinType === "P"}
                                />
                                &nbsp;&nbsp; SSN
                            </Col>
                            <Col md={6}>
                                <input
                                    type="radio"
                                    name="tinType"
                                    value="T"
                                    onChange={handleRadioChange}
                                    checked={user.tinType === "T"}
                                />
                                &nbsp;&nbsp; TIN/EIN
                                <QuestionCircle
                                    tabIndex="-1"
                                    className="iconClass"
                                    data-tip="The TIN/EIN is the tax id for a trust or organization."
                                />
                                <span className="validation-error-message">
                                    {errors.tinType}
                                </span>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={4}>
                                <Form.Label>
                                    Policyowner's Tax ID
                                    <QuestionCircle
                                        tabIndex="-1"
                                        className="iconClass"
                                        data-tip="If the policyowner does not have a Social Security Number (SSN) or Tax Identification Number (TIN), please contact Customer Service: (800) 347-7787."
                                    />
                                </Form.Label>
                                <Form.Control
                                    name="tin"
                                    autoComplete="off"
                                    maxLength={11}
                                    value={tinDisplay}
                                    onChange={handleTinChange}
                                    onFocus={handleTinFocus}
                                    onBlur={handleTinBlur}
                                />
                                <span className="validation-error-message">
                                    {errors.tin}
                                </span>
                            </Col>
                            <Col md={4}>
                                <Form.Label>Policy Number</Form.Label>
                                <Form.Control
                                    name="policyNumber"
                                    autoComplete="off"
                                    value={user.policyNumber}
                                    disabled
                                />
                                <span className="validation-error-message">
                                    {errors.policyNumber}
                                </span>
                            </Col>
                        </Row>
                        {user.tinType === "T" && (
                            <Row>
                                <Col md={4}>
                                    <Form.Label>
                                        Trust/Organization (Optional)
                                    </Form.Label>
                                    <Form.Control
                                        name="trustName"
                                        autoComplete="off"
                                        maxLength={30}
                                        value={user.trustName}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                    />
                                    <span className="validation-error-message">
                                        {errors.trustName}
                                    </span>
                                </Col>
                            </Row>
                        )}

                        <Row>
                            <Col sm={12}>
                                <label className="sub-title">
                                    Contact Information
                                </label>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Label>First Name</Form.Label>
                                <Form.Control
                                    name="firstName"
                                    autoComplete="off"
                                    value={user.firstName}
                                    disabled
                                />
                            </Col>
                            <Col md={6}>
                                <Form.Label>Last Name</Form.Label>
                                <Form.Control
                                    name="lastName"
                                    autoComplete="off"
                                    value={user.lastName}
                                    disabled
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Label>Middle Name (Optional)</Form.Label>
                                <Form.Control
                                    name="middleName"
                                    autoComplete="off"
                                    maxLength={25}
                                    value={user.middleName}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    disabled={isMiddleNameDisabled}
                                />
                                <span className="validation-error-message">
                                    {errors.middleName}
                                </span>
                            </Col>
                            <Col md={6}>
                                <Form.Label>Suffix (Optional)</Form.Label>
                                <Form.Control
                                    name="suffix"
                                    autoComplete="off"
                                    maxLength={35}
                                    value={user.suffix}
                                    onChange={handleChange}
                                    disabled={isSuffixDisabled}
                                />
                                <span className="validation-error-message">
                                    {errors.suffix}
                                </span>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <ReactTooltip />
                                <Form.Label className="emailClass">
                                    Email
                                </Form.Label>
                                <Form.Control
                                    name="email"
                                    autoComplete="off"
                                    value={user.email}
                                    disabled
                                />
                                <span className="validation-error-message">
                                    {errors.email}
                                </span>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <ReactTooltip />
                                <Form.Label className="createUsername">
                                    Username
                                    <QuestionCircle
                                        tabIndex="-1"
                                        className="iconClass"
                                        data-tip="Remember this! You will need it along with your password to access your account information."
                                    />
                                </Form.Label>
                                <Form.Control
                                    name="id"
                                    autoComplete="off"
                                    maxLength={20}
                                    value={user.id}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                />
                                <span className="validation-error-message">
                                    {errors.id}
                                </span>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={12}>
                                <input
                                    type="checkbox"
                                    name="isCheckedFindMyPolicy"
                                    checked={user.isCheckedFindMyPolicy}
                                    onChange={handleCheckboxChange}
                                    onClick={handleBlur}
                                />
                                &nbsp;&nbsp;By clicking on "Register", you agree
                                to the{" "}
                                <a href="#" onClick={setTermsAndConditions}>
                                    {" "}
                                    Terms and Conditions
                                </a>{" "}
                                of this site.
                                <br />
                                <TermsAndConditions
                                    TermsAndConditionsOpen={
                                        termsAndConditionsOpen
                                    }
                                    CloseTermsAndConditions={
                                        closeTermsAndConditions
                                    }
                                ></TermsAndConditions>
                                <span className="validation-error-message">
                                    {errors.isCheckedFindMyPolicy}
                                </span>
                            </Col>
                        </Row>

                        <div className="buttons-myaccount">
                            <Button
                                variant="primary"
                                type="submit"
                                onClick={handleSubmit}
                                data-track="analytics"
                                disabled={
                                    submitDisabled ||
                                    submitting ||
                                    submitSuccessful
                                }
                            >
                                {submitting ? "Registering" : "Register"}
                            </Button>
                        </div>
                    </Form>

                    <DisclaimerPolicyOwner />
                </div>
                <Footer />
            </div>
            {showSuccessOverlay && (
                <div
                    style={{
                        position: "fixed",
                        inset: 0,
                        zIndex: 2000,
                        backgroundColor: "rgba(0, 0, 0, 0.45)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "24px"
                    }}
                >
                    <div
                        style={{
                            maxWidth: "760px",
                            width: "100%",
                            background: "#ffffff",
                            borderRadius: "12px",
                            padding: "28px 24px",
                            textAlign: "center",
                            boxShadow: "0 14px 40px rgba(0, 0, 0, 0.3)",
                            color: "#1f2a44",
                            fontSize: "18px",
                            lineHeight: "1.5",
                            fontWeight: 600
                        }}
                    >
                        <div>
                            Your registration was successful! You will now be
                            redirected to choosing a new password for your
                            account and setting up MFA to secure it.
                        </div>
                        <div
                            style={{
                                fontSize: "14px",
                                fontWeight: 400,
                                color: "#666",
                                marginTop: "20px",
                                paddingTop: "20px",
                                borderTop: "1px solid #ddd"
                            }}
                        >
                            If you're not redirected in 5 seconds,{" "}
                            <a
                                href={successRedirectUrl}
                                style={{
                                    color: "#007bff",
                                    textDecoration: "none",
                                    fontWeight: 500
                                }}
                            >
                                click here
                            </a>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default OHHAutoReg;
